import React, { Component } from 'react';

import { HotmartRum } from '@hot-observability-js/react';
import { ButtonIcon } from 'components/button';
import { I18n } from 'components/translate';
import HttpCodes from 'configs/HttpCodes';
import { DatahubEventsService } from 'modules/app/services';
import { GROUPS, NAMES } from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import TryDownloadSubtitlesSchema from 'modules/app/services/datahubEvents/media/tryDownloadSubtitlesSchema';
import { ACTIONS, CATEGORIES, EVENTS } from 'modules/app/services/ga/constants';
import { VALID_FILE_REQUEST_STATUS } from 'modules/file/constants';
import { MembershipStore } from 'modules/membership';
import { SUBTITLE_STATUS, SUBTITLE_TYPE } from 'modules/subtitle/constants';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import SubtitleActionButton from './SubtitleActionButton';
import SubtitleCard from './SubtitleCard';

import SubtitleActions from '../../SubtitleActions';
import SubtitleEnable from '../SubtitleEnable';

import { axios } from '@hotmart/request';

import './SubtitleSection.scss';

export default class SubtitleItem extends Component {
  static propTypes = {
    languageCode: PropTypes.string,
    onReview: PropTypes.func,
    onTranslate: PropTypes.func,
    onDownload: PropTypes.func,
    reviewed: PropTypes.bool,
    mediaId: PropTypes.string,
    status: PropTypes.string,
    url: PropTypes.string,
    videoName: PropTypes.string,
    type: PropTypes.string,
    id: PropTypes.string
  };

  state = {
    subtitleURI: '',
    downloadingSubtitle: false
  };

  set subtitleURI(assetURI) {
    this.setState({
      subtitleURI: assetURI
    });
  }

  get subtitleURI() {
    return this.state.subtitleURI || false;
  }

  set isDownloadingSubtitle(newstate) {
    this.setState({
      downloadingSubtitle: newstate
    });
  }

  get isDownloadingSubtitle() {
    return this.state.downloadingSubtitle;
  }

  get isError() {
    return this.props.status === SUBTITLE_STATUS.ERROR;
  }

  get isProcessing() {
    return this.props.status === SUBTITLE_STATUS.PROCESSING;
  }

  get isReady() {
    return !(this.isError || this.isProcessing);
  }

  get typeLabel() {
    const { type, reviewed } = this.props;

    let label = type;

    if (type !== SUBTITLE_TYPE.UPLOAD && reviewed) {
      label += '_REVIEWED';
    }

    return label;
  }

  render() {
    return (
      <li className="subtitle__list__item">
        <SubtitleCard small className="subtitle__item__card">
          {this.renderHeader()}
          {this.renderContent()}

          {this.isProcessing && this.renderProcessing()}
          {!this.isProcessing && this.renderActions()}
        </SubtitleCard>
      </li>
    );
  }

  renderHeader() {
    return (
      <SubtitleCard.Header
        label={`player_subtitles.type_description.${this.typeLabel}`}
      >
        {this.isError && (
          <label className="subtitle__status subtitle__status--error">
            <i className="icon icon-subtitle-error subtitle__status__icon" />
            <span className="subtitle__status__label">
              <I18n label="player_subtitles.status_description.FAILED" />
            </span>
          </label>
        )}

        {this.isProcessing && (
          <ButtonIcon
            className="subtitle__status subtitle__status--processing"
            icon="processing"
            transparent
          />
        )}

        {this.isReady && (
          <ButtonIcon
            className="subtitle__remove"
            icon="delete"
            transparent
            onClick={this.onRemove}
          />
        )}
      </SubtitleCard.Header>
    );
  }

  renderContent() {
    const { languageCode, mediaId, id, status } = this.props;

    return (
      <SubtitleCard.Content>
        <p className="subtitle__item__language">
          <I18n label={`supported_languages.${languageCode}`} />
        </p>

        {this.isReady && (
          <SubtitleEnable mediaId={mediaId} status={status} id={id} />
        )}
      </SubtitleCard.Content>
    );
  }

  renderProcessing() {
    return (
      <label className="label__description">
        <I18n label="player_subtitles.status_description.PROCESSING" />
      </label>
    );
  }

  renderActions() {
    let actions = [];

    if (this.isError) {
      actions = [
        <SubtitleActionButton
          key="first"
          name="retry"
          label="general.retry"
          onClick={this.onRetry}
        />,
        <SubtitleActionButton
          key="second"
          name="delete"
          label="general.remove"
          onClick={this.onRemove}
        />
      ];
    }

    if (this.isReady) {
      actions = [
        <SubtitleActionButton
          key="first"
          name="edit"
          label="general.review"
          onClick={this.onReview}
        />,
        <SubtitleActionButton
          key="second"
          name="translate"
          label="player_subtitles.translate.label"
          onClick={this.onTranslate}
        />,
        this.isDownloadingSubtitle ? (
          <SubtitleActionButton
            name="spinner-third"
            label="player_subtitles.downloading_subtitle"
            disabled
          />
        ) : (
          <SubtitleActionButton
            name="download"
            label="player_subtitles.download_subtitle"
            onClick={this.onDownload}
          />
        )
      ];
    }

    return <div className="subtitle__item__actions">{actions}</div>;
  }

  registerMetrics = async (language) => {
    const { marketplaceId } = MembershipStore;

    if (marketplaceId) {
      const DATAHUB_EVENT = {
        group: GROUPS.SUBTITLE,
        name: NAMES.DOWNLOAD_SUBTITLE,
        user: { id: marketplaceId },
        params: { language }
      };

      const GTM_EVENT = {
        event: EVENTS.INTERACTION,
        event_category: CATEGORIES.CLUB_ADMIN_CONTENT,
        event_action: `${ACTIONS.SUBTITLE_DOWNLOAD}:${language}`,
        event_label: true
      };

      try {
        window.dataLayer.push(GTM_EVENT);
        DatahubService.sendMetrify(DATAHUB_EVENT);
      } catch {
        // do nothing
      }
    }
  };

  getSanitizedVideoName = () => {
    const { videoName = 'subtitle' } = this.props;
    const regEx = /[a-z0-9_-]/gim;

    return videoName.match(regEx).join('');
  };

  getDownloadFileName = () => {
    const { languageCode = '' } = this.props;

    return `${this.getSanitizedVideoName()}_${languageCode}`;
  };

  fetchSubtitleFile = () => {
    const { url: subtitleUrl } = this.props;

    return axios
      .get(subtitleUrl)
      .then((response) => {
        if (VALID_FILE_REQUEST_STATUS.includes(response.status)) {
          const fileBlob = new Blob([response.data], {
            type: 'application/octet-stream'
          });
          const fileURI = URL.createObjectURL(fileBlob);

          return fileURI;
        }
        throw new Error(response);
      })
      .catch((error) => {
        HotmartRum.captureException(error);
        UiService.setAlertError('subtitle_unknown_error.download');

        return false;
      });
  };

  downloadFile = (localFileURI) => {
    const fileLink = document.createElement('a');
    const newFileName = this.getDownloadFileName();

    fileLink.setAttribute('href', localFileURI);
    fileLink.setAttribute('download', `${newFileName}.vtt`);
    fileLink.click();
  };

  onDownload = async () => {
    const { languageCode = '' } = this.props;
    let localFileURI = this.subtitleURI;

    this.isDownloadingSubtitle = true;
    this.registerMetrics(languageCode);

    if (!localFileURI) {
      localFileURI = await this.fetchSubtitleFile();
    }

    if (localFileURI) {
      this.subtitleURI = localFileURI;
      this.downloadFile(localFileURI);
    }

    DatahubEventsService.sendEvent({
      schema: new TryDownloadSubtitlesSchema({
        statusCode: HttpCodes.OK,
        result: 'SUCCESSFUL'
      })
    });

    this.isDownloadingSubtitle = false;
  };

  onTranslate = () => {
    const { id } = this.props;

    this.props.onTranslate && this.props.onTranslate(id);
  };

  onReview = () => {
    const { id } = this.props;

    this.props.onReview && this.props.onReview(id);
  };

  onRetry = () => {
    const { mediaId, id } = this.props;

    SubtitleActions.retrySubtitle(id, mediaId);
  };

  onRemove = () => {
    const { mediaId, id } = this.props;

    SubtitleActions.removeSubtitle(mediaId, id);
  };
}
